import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Loader2, Mail, Send } from "lucide-react";
import { formatApiError } from "../../lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";

const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-AU", { dateStyle: "medium" });
const families = (n) => `${n} ${n === 1 ? "family" : "families"}`;

const buttonLabel = (info, running, sendLabel) => {
  if (running) return "Sending…";
  if (info.sent_count > 0 && info.eligible > 0) return `Send to ${info.eligible} new signup${info.eligible === 1 ? "" : "s"}`;
  return sendLabel;
};

const Progress = ({ job, prefix }) => {
  const total = Math.max(job.total, job.sent + job.failed);
  return (
    <p data-testid={`${prefix}-progress`} role="status" aria-live="polite" className="status-pill mt-5 bg-[#FAF3E8] text-[#6B4A1F]">
      <Loader2 className="h-5 w-5 shrink-0 animate-spin" />
      Sending… {job.sent} of {total} sent{job.waiting_until ? " — waiting a moment for the email limit" : ""}
    </p>
  );
};

const Preview = ({ info, prefix }) => (
  <div className="mt-6 overflow-hidden rounded-2xl border border-[#E6DEC6]">
    <p className="flex items-center gap-2 border-b border-[#E6DEC6] bg-[#F7F2E9] px-5 py-3 text-sm text-[#5C4F4A]">
      <Mail className="h-4 w-4" /> Subject: <strong data-testid={`${prefix}-subject`} className="text-[#2C221E]">{info.subject}</strong>
    </p>
    <iframe title={`${prefix} email preview`} data-testid={`${prefix}-preview`} srcDoc={info.html} sandbox="" className="h-[560px] w-full bg-[#FDFBF7]" />
  </div>
);

export const CampaignPanel = ({ token, onUnauthorized, prefix, eyebrow, title, sendLabel, fetcher, sender, children }) => {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [confirm, setConfirm] = useState(false);

  const fail = useCallback((err) => {
    if (err.response?.status === 401) return onUnauthorized();
    setError(formatApiError(err.response?.data?.detail));
  }, [onUnauthorized]);
  const load = useCallback(() => fetcher(token).then(({ data }) => setInfo(data)).catch(fail), [token, fetcher, fail]);

  useEffect(() => { load(); }, [load]);
  const running = !!info?.job?.running;
  useEffect(() => {
    if (!running) return;
    const t = setInterval(load, 1500);
    return () => clearInterval(t);
  }, [running, load]);

  const send = async () => {
    setConfirm(false);
    setError("");
    try {
      const { data } = await sender(token);
      if (!data.started) setError(data.message);
      load();
    } catch (err) {
      fail(err);
    }
  };

  if (!info) return <div className="warm-card mt-10 p-8 text-[#8A7A72]">{error || "Loading…"}</div>;
  const allSent = info.sent_count > 0 && info.eligible === 0;

  return (
    <section data-testid={`${prefix}-panel`} className="warm-card mt-10 p-6 sm:p-8 hover:translate-y-0">
      <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="eyebrow">{eyebrow}</p>
          <h2 className="mt-2 font-serif text-2xl text-[#2C221E]">{title}</h2>
          <p data-testid={`${prefix}-recipient-count`} className="mt-2 text-sm text-[#5C4F4A]">
            Recipients: <strong>{info.eligible}</strong> of {info.total} signups
          </p>
        </div>
        <button onClick={() => setConfirm(true)} disabled={running || info.eligible === 0} data-testid={`${prefix}-send-button`} className="btn-primary">
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {buttonLabel(info, running, sendLabel)}
        </button>
      </div>
      {running && <Progress job={info.job} prefix={prefix} />}
      {allSent && !running && (
        <p data-testid={`${prefix}-sent-status`} className="status-pill mt-5 bg-[#EAF0EC] text-[#2F4537]">
          <CheckCircle2 className="h-5 w-5" /> Sent to {info.sent_count} families on {fmtDate(info.last_sent_at)}
        </p>
      )}
      {info.job && !running && (
        <p data-testid={`${prefix}-report`} className="mt-3 text-sm text-[#5C4F4A]">Last send: {info.job.sent} sent, {info.job.failed} failed</p>
      )}
      {info.total === 0 && <p className="mt-3 text-sm text-[#8A7A72]">No signups yet.</p>}
      {error && <p role="alert" data-testid={`${prefix}-error`} className="mt-3 text-sm text-[#B24E2A]">{error}</p>}
      {children?.(info, load)}
      <Preview info={info} prefix={prefix} />

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid={`${prefix}-confirm-dialog`} className="rounded-3xl bg-[#FDFBF7]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Send "{info.subject}"?</AlertDialogTitle>
            <AlertDialogDescription data-testid={`${prefix}-confirm-count`}>
              "{info.subject}" will be sent to {families(info.eligible)}. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${prefix}-confirm-cancel`} className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid={`${prefix}-confirm-send`} onClick={send} className="rounded-full bg-[#C85A32] hover:bg-[#B24E2A]">
              Send now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};
